import React, { useContext, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { useApiErrorHandler } from "@/lib/errorHandler";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { CaseTimer, caseTimerApi } from "@/api/caseTimer";
import { useTranslation } from "react-i18next";

export function DeleteCaseTimer({
  trigger,
  caseTimer,
  refresh,
}: {
  trigger: JSX.Element;
  caseTimer: CaseTimer;
  refresh?: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { values: contextValues, set } = useContext(
    AuthContext,
  ) as AuthContextType;

  const { loading, user } = contextValues;
  const { setLoading } = set;

  const errorHandler = useApiErrorHandler("DeleteCaseTimer", {});

  async function onSubmit() {
    try {
      setLoading?.(true);
      await caseTimerApi.delete({
        id: caseTimer.id.toString(),
        token: user?.access,
        data: {
          deleted_by: user?.user.id,
        },
      });
      toast({
        title: t("common.toast.deleted_successfully", {
          resource: t("casetimer.title.timer"),
        }),
      });
      await refresh?.();
      setOpen(false);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading?.(false);
    }
  }

  function onCancel() {
    setOpen(false);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="w-full md:w-[500px] max-h-screen overflow-y-auto"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t("casetimer.title.delete_case_timer")}</DialogTitle>
          <DialogDescription>
            {t("common.statictext.confirm_delete", {
              name:
                caseTimer.description +
                " " +
                caseTimer.end_time +
                "-" +
                caseTimer.start_time,
            })}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="secondary" disabled={loading} onClick={onCancel}>
            {loading ? t("common.button.loading") : t("common.button.cancel")}
          </Button>
          <Button variant="destructive" disabled={loading} onClick={onSubmit}>
            {loading ? t("common.button.loading") : t("common.button.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
